import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, Search, X, Check } from "lucide-react";
import { getMethodApiCall } from "src/services/api";

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/**
 * Searchable dropdown for MCC student list filters (registration_id / username).
 * Loads options from GET /v1/api/mcc/students/suggestions while typing.
 */
export default function StudentFilterSelect({
  label,
  field,
  value,
  onChange,
  placeholder = "Select or search…",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, 300);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);

  const allLabel = "All";

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) setSearchTerm("");
  }, [isOpen, field]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await getMethodApiCall("/v1/api/mcc/students/suggestions", {
          field,
          q: debouncedSearch,
        });
        if (cancelled) return;
        if (res?.status === 200 && res?.data?.success) {
          const list = res.data.data || [];
          setOptions(
            Array.isArray(list)
              ? list.map((item) =>
                  typeof item === "string"
                    ? { label: item, value: item }
                    : item
                )
              : []
          );
        } else {
          setOptions([]);
        }
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, debouncedSearch, field]);

  const withAll = [{ label: allLabel, value: "" }, ...options];
  const selectedLabel = withAll.find((opt) => opt.value === value)?.label;

  const handleSelect = (selectedValue) => {
    onChange(selectedValue);
    setIsOpen(false);
    setSearchTerm("");
  };

  return (
    <div className="w-full relative" ref={wrapperRef}>
      {label && (
        <label className="text-xs font-semibold text-primary mb-1.5 block">
          {label}
        </label>
      )}

      <div
        className={`w-full px-3 py-2 bg-white border rounded-md ${
          "cursor-pointer"
        } flex items-center justify-between text-sm transition-all
          ${
            isOpen
              ? "border-primary ring-1 ring-primary"
              : "border-gray-300 hover:border-gray-400"
          }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span
          className={`truncate ${
            !selectedLabel ? "text-gray-400" : "text-gray-700"
          }`}
        >
          {selectedLabel || placeholder}
        </span>
        <ChevronDown
          size={16}
          className={`text-gray-500 transition-transform shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 flex flex-col">
          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white z-10">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-2.5 top-2.5 text-gray-400"
              />
              <input
                type="text"
                className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-200 rounded bg-gray-50 focus:outline-none focus:border-primary focus:bg-white transition-colors"
                placeholder="Type to search…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchTerm("");
                  }}
                  className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          <div className="overflow-y-auto flex-1 py-1">
            {loading && (
              <div className="px-3 py-3 text-center text-sm text-gray-400">
                Loading…
              </div>
            )}
            {!loading &&
              withAll.map((opt, i) => {
                const isSelected = value === opt.value;
                return (
                  <div
                    title={opt.label}
                    key={`${opt.value}-${i}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelect(opt.value);
                    }}
                    className={`px-3 py-2 text-sm cursor-pointer flex items-center justify-between transition-colors
                      ${
                        isSelected
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-gray-700 hover:bg-gray-50"
                      }
                    `}
                  >
                    <span className="truncate mr-2">{opt.label}</span>
                    {isSelected && (
                      <Check size={14} className="text-primary shrink-0" />
                    )}
                  </div>
                );
              })}
            {!loading &&
              options.length === 0 &&
              debouncedSearch.trim().length > 0 && (
                <div className="px-3 py-2 text-xs text-gray-400 border-t border-gray-100">
                  No matching values in database.
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
}
